import { CloseOutlined, ExportOutlined } from '@ant-design/icons';
import { useQuery } from '@apollo/client/react';
import { css } from '@emotion/css';
import { Alert, Button, Space } from 'antd';
import isEqual from 'lodash/isEqual';
import type React from 'react';
import { useCallback, useEffect, useState } from 'react';
import { SystemStatusType } from './graphql/generated/graphql.ts';
import { SYSTEM_STATUS_QUERY } from './SystemStatusBanner.gql';
import type { UpdateEvent } from './socket/types.ts';
import { useSubscription } from './socket/useSubscription.ts';
import { openExternalLink } from './utils/linkOpener.ts';

export const SystemStatusBanner: React.FC = () => {
  const [closed, setClosed] = useState(false);
  const { data, previousData, refetch } = useQuery(SYSTEM_STATUS_QUERY);

  useEffect(() => {
    if (!isEqual(data?.systemStatus, previousData?.systemStatus)) {
      setClosed(false);
    }
  }, [data, previousData]);

  const callback = useCallback(
    (event: UpdateEvent) => {
      console.debug(`received UI update: ${JSON.stringify(event)}`);
      refetch();
    },
    [refetch]
  );

  useSubscription({ type: 'system-status-updated' }, callback);

  let type: 'info' | 'warning' | 'error' = 'info';
  if (data?.systemStatus?.type === SystemStatusType.ERROR) {
    type = 'error';
  } else if (data?.systemStatus?.type === SystemStatusType.WARNING) {
    type = 'warning';
  }

  const visible = data?.systemStatus?.active && !closed;

  return (
    <div
      className={css`
        max-height: ${visible ? '300px' : 0};
        opacity: ${visible ? 1 : 0};
        transition-property: max-height, opacity;
        transition-duration: 0.5s;
        transition-timing-function: linear;
      `}
    >
      <Alert
        showIcon
        banner
        type={type}
        title={data?.systemStatus?.heading}
        description={
          <div
            className={css`
              white-space: pre-wrap;
            `}
          >
            {data?.systemStatus?.content}
          </div>
        }
        action={
          <Space size="small">
            {data?.systemStatus?.link && (
              <Button
                icon={<ExportOutlined />}
                onClick={() => (data?.systemStatus?.link ? openExternalLink(data.systemStatus.link) : null)}
              >
                Weitere Informationen
              </Button>
            )}
            <Button type="text" icon={<CloseOutlined />} onClick={() => setClosed(true)} />
          </Space>
        }
        className={css`
          .ant-alert-title {
            white-space: nowrap;
          }

          .ant-alert-description {
            max-height: 100px;
            overflow: auto;
          }
        `}
      />
    </div>
  );
};
